// Brand mark for a connected-service provider (plans/connections-design.md §1.1).
//
// Brand rules this component enforces by construction:
// - Google: the standard four-color "G", unaltered: never recolored, never
//   monochrome, no disc or badge behind it. Never the Calendar/Tasks product
//   icons; the words "Google Calendar" / "Google Tasks" sit next to the G.
// - Todoist: the logomark (red #E44332 rounded square, white stacked checks),
//   unaltered. The square IS the logo, not a badge we added. No outline,
//   shadow or opacity change.
// - Apple: NO Apple logo and NO Calendar/Reminders app icons (Apple's
//   trademark guidelines). Neutral lucide glyphs at 0.62 white instead.
//
// The mark is always decorative (aria-hidden): every placement pairs it with
// the provider's name in text, which is what assistive tech reads. Minimum
// size is 12px; the 25% clear space around the G is the caller's layout.
//
// Placements are fixed by the spec: provider rows (20), the Tasks titlebar,
// list menu and Save-to picker (12), the explainer's mark disc (20), plus the
// Google sign-in button. Never on task or event rows.

import React from 'react';
import { CalendarDays, ListChecks } from 'lucide-react';

export type Provider = 'google' | 'todoist' | 'apple-calendar' | 'apple-reminders';

/** The three sizes the spec allows. 12 is the brand minimum for G and Todoist. */
export type ProviderMarkSize = 12 | 16 | 20;

export interface ProviderMarkProps {
  provider: Provider;
  /** Rendered box in CSS px. Default 20. */
  size?: ProviderMarkSize;
  /** Layout class only (margins, flex). Never recolor or fade a brand mark. */
  className?: string;
}

/** Neutral glyph color for the Apple rows (§1.1, §1.2: 0.62 clears AA). */
const APPLE_GLYPH_COLOR = 'rgba(255, 255, 255, 0.62)';

export const ProviderMark: React.FC<ProviderMarkProps> = ({ provider, size = 20, className }) => {
  switch (provider) {
    case 'google':
      return (
        <svg
          className={className}
          width={size}
          height={size}
          viewBox="0 0 24 24"
          aria-hidden="true"
          focusable="false"
          data-provider="google"
        >
          <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
          <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
          <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
          <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
        </svg>
      );
    case 'todoist':
      // Logomark geometry: Simple Icons "todoist" (CC0), which traces Doist's
      // mark. The checks are cut out of the square, so a white plate sits
      // behind them. The plate stays clear of the rounded corners so no white
      // fringe shows at the antialiased edge.
      return (
        <svg
          className={className}
          width={size}
          height={size}
          viewBox="0 0 24 24"
          aria-hidden="true"
          focusable="false"
          data-provider="todoist"
        >
          <rect x="0" y="4" width="15" height="17" fill="#FFFFFF" />
          <path
            fill="#E44332"
            d="M21 0H3C1.35 0 0 1.35 0 3v3.858s3.854 2.24 4.098 2.38c.31.18.694.177 1.004 0 .26-.147 8.02-4.608 8.136-4.675.279-.161.58-.107.748-.01.164.097.606.348.84.48.232.134.221.502.013.622l-9.712 5.59c-.346.2-.69.204-1.048.002C3.478 10.907.998 9.463 0 8.882v2.02l4.098 2.38c.31.18.694.177 1.004 0 .26-.147 8.02-4.609 8.136-4.676.279-.16.58-.106.748-.008.164.096.606.347.84.48.232.133.221.5.013.62-.208.121-9.288 5.346-9.712 5.59-.346.2-.69.205-1.048.002C3.478 14.951.998 13.506 0 12.926v2.02l4.098 2.38c.31.18.694.177 1.004 0 .26-.147 8.02-4.609 8.136-4.676.279-.16.58-.106.748-.009.164.097.606.348.84.48.232.133.221.502.013.622l-9.712 5.59c-.346.199-.69.204-1.048.001C3.478 18.994.998 17.55 0 16.97V21c0 1.65 1.35 3 3 3h18c1.65 0 3-1.35 3-3V3c0-1.65-1.35-3-3-3z"
          />
        </svg>
      );
    case 'apple-calendar':
      return (
        <CalendarDays
          className={className}
          size={size}
          strokeWidth={1.75}
          color={APPLE_GLYPH_COLOR}
          aria-hidden="true"
          focusable="false"
          data-provider="apple-calendar"
        />
      );
    case 'apple-reminders':
      return (
        <ListChecks
          className={className}
          size={size}
          strokeWidth={1.75}
          color={APPLE_GLYPH_COLOR}
          aria-hidden="true"
          focusable="false"
          data-provider="apple-reminders"
        />
      );
  }
};

export default ProviderMark;
